import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * THE BAR-ANCHORED GANTT TOOLTIP — the hover card that sits centred ABOVE a
 * Gantt bar, plus the hover bridge and the delayed hide that let the pointer
 * travel from the bar into the card (and click its buttons). Shared by the
 * Crew Gantt (`budget/CrewGanttView.js`), the hires Gantt
 * (`hires/GanttView.js`) and the cross-team task Gantt
 * (`teams/CrossTeamGanttView.js`). Extracted 2026-10-05 (owner: *"check all
 * gannt charts for all the updates we are making. should be common
 * code/fixes where possible"*) from the crew and hires copies — the declared
 * duplication groups `229bec9a` (showTooltip) and `4acbc27d` (hideTooltip).
 *
 * 🔑 THE GEOMETRY IS ONE CONTRACT, NOT THREE NUMBERS. `placeGanttTooltip`
 * puts the card's edge `GANTT_TOOLTIP_GAP_PX` (8) from the bar — its bottom
 * 8px above, or (flipped under the pinned band) its top 8px below — and the
 * bridge is `h-2` (8px) hanging from the card's BAR side — it covers EXACTLY
 * the gap. (Until 2026-10-05 the card was `translate(-50%, -100%)` off an
 * anchor point; the placement function replaced that, the contract held.) It was `h-4` (16px) on both charts and overlapped
 * the bar's top 8px, swallowing the mousedown meant for a drag/resize (owner
 * 2026-10-05: *"tool tip cover the bar when i try to click to drag to make it
 * longer"*, fixed in c4d2ef7e/465ad7f9). Change one of the three and change
 * the others.
 *
 * 🔑 PORTALLED to `document.body` at the z-60 ESCAPE TIER
 * (`components/ui/CLAUDE.md`'s z-index contract): a chart root that is
 * `isolate` (every chart with a pinned axis) traps an in-tree `fixed` child
 * under the app bar (z-30) and the toolbar (z-20).
 *
 * The CONTENT is the caller's (`children`) — each chart's lines differ.
 * `onMouseLeave` is the caller's too: the crew and hires cards close at once,
 * the cross-team card keeps the delayed hide.
 */

/** The gap between the bar's top edge and the card's bottom edge. */
export const GANTT_TOOLTIP_GAP_PX = 8;
/** The grace period before a left bar's tooltip closes. */
export const GANTT_TOOLTIP_HIDE_MS = 200;
/** The furthest a card's centre may sit sideways from the pointer (owner 2026-10-05). */
export const GANTT_TOOLTIP_MAX_POINTER_OFFSET_PX = 120;

/**
 * `useGanttBarTooltip(blocked)` → `{ tooltip, setTooltip, show, hide, cancelHide }`.
 * `blocked` (a drag in progress) suppresses `show` and `hide`, exactly as the
 * two original copies did. `show(e, payload)` merges `payload` with the
 * anchor `{ x, y }` read from `e.currentTarget`.
 */
export function useGanttBarTooltip(blocked) {
  const [tooltip, setTooltip] = useState(null);
  const hideTimeout = useRef(null);

  useEffect(() => () => {
    if (hideTimeout.current) clearTimeout(hideTimeout.current);
  }, []);

  function cancelHide() {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
      hideTimeout.current = null;
    }
  }

  function show(e, payload) {
    if (blocked) return;
    cancelHide();
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      ...payload,
      // The bar's rect — `placeGanttTooltip` decides above/below and clamps.
      anchor: { ...rectOf(rect), pointerX: e.clientX },
      x: rect.left + rect.width / 2,
      y: rect.top - GANTT_TOOLTIP_GAP_PX,
    });
  }

  function hide() {
    if (!blocked) {
      // Delay hiding so the pointer can move from the bar onto the card.
      hideTimeout.current = setTimeout(() => {
        setTooltip(null);
        hideTimeout.current = null;
      }, GANTT_TOOLTIP_HIDE_MS);
    }
  }

  return { tooltip, setTooltip, show, hide, cancelHide };
}

const VIEWPORT_MARGIN_PX = 8;

function rectOf(r) {
  return { left: r.left, top: r.top, width: r.width, height: r.height, bottom: r.top + r.height };
}

/**
 * THE ONE ANCHOR RULE for every Gantt card — the hover card AND the drag card
 * (owner 2026-10-05: *"the tool tips when dragging are appearing in what seem to
 * be random places? I think they should appear anchored either above or below
 * the bar"*). Centred over the bar, `GANTT_TOOLTIP_GAP_PX` ABOVE it; BELOW it
 * when the space above would put the card under `minTop` (the bottom of the
 * pinned toolbar / date band, MEASURED by the caller — never the viewport top);
 * clamped horizontally inside the viewport. Pure: `anchor` and `tip` are
 * `{ left, top, width, height }` boxes.
 */
export function placeGanttTooltip(anchor, tip, {
  minTop = 0,
  gap = GANTT_TOOLTIP_GAP_PX,
  viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0,
  viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 0,
  pointerX = null,
  // 'below' (2026-10-06, owner: the crew Gantt's drag card "should be below the
  // days") puts the card UNDER the anchor, flipping above only when it would
  // run off the bottom of the viewport. Default 'above' is unchanged.
  prefer = 'above',
} = {}) {
  let centre = anchor.left + anchor.width / 2;
  // A LONG bar's centre can be off-screen or far from the hand (owner
  // 2026-10-05: "centre is nice until it's too far away so there should be a
  // distance limit"): keep the centre within GANTT_TOOLTIP_MAX_POINTER_OFFSET_PX
  // of the pointer; the viewport clamp below keeps an off-screen centre on
  // screen. (A separate "visible part of the bar" clamp lived here until
  // 2026-10-05 and was DELETED as output-inert — a 2,000,000-case fuzz found 0
  // differences, because the viewport clamp maps every centre it produced to
  // the same `left`. Pre-deletion SHA 8dcff391.)
  if (Number.isFinite(pointerX)) {
    centre = Math.min(Math.max(centre, pointerX - GANTT_TOOLTIP_MAX_POINTER_OFFSET_PX), pointerX + GANTT_TOOLTIP_MAX_POINTER_OFFSET_PX);
  }
  const maxLeft = Math.max(VIEWPORT_MARGIN_PX, viewportWidth - VIEWPORT_MARGIN_PX - tip.width);
  const left = Math.min(Math.max(centre - tip.width / 2, VIEWPORT_MARGIN_PX), maxLeft);
  const aboveTop = anchor.top - gap - tip.height;
  const belowTop = anchor.top + anchor.height + gap;
  if (prefer === 'below') {
    const fitsBelow = !viewportHeight || belowTop + tip.height <= viewportHeight;
    if (fitsBelow || aboveTop < minTop) return { left, top: belowTop, side: 'below' };
    return { left, top: aboveTop, side: 'above' };
  }
  if (aboveTop >= minTop) return { left, top: aboveTop, side: 'above' };
  return { left, top: belowTop, side: 'below' };
}

const CARD = 'fixed bg-gray-900 text-white text-xs rounded-lg shadow-xl';

/** The HOVER card. `getMinTop` (optional) returns the pinned chrome's bottom. */
export default function GanttBarTooltip({
  tooltip,
  onMouseEnter,
  onMouseLeave,
  testId,
  className = 'px-3 py-2',
  maxWidth = 280,
  getMinTop,
  children,
}) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  /* Placed ONCE per anchor (a new bar hovered), never on every render: a
     `fixed` box near the viewport's right edge shrinks to fit, so its measured
     size depends on its own `left` — re-placing after every render let width,
     height and side chase each other ("Maximum update depth exceeded", owner,
     2026-10-05). `width: max-content` (capped by `maxWidth`) also makes the
     size independent of `left`. */
  const anchor = tooltip.anchor;
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !anchor) return;
    const tip = el.getBoundingClientRect();
    setPos(placeGanttTooltip(anchor, tip, { minTop: getMinTop ? getMinTop() : 0, pointerX: anchor.pointerX }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor]);
  const side = pos ? pos.side : 'above';
  return createPortal((
    <div
      ref={ref}
      className={`${CARD} ${className}`}
      style={{
        zIndex: 60,
        left: pos ? pos.left : 0,
        top: pos ? pos.top : 0,
        visibility: pos ? 'visible' : 'hidden',
        width: 'max-content',
        maxWidth,
      }}
      data-testid={testId}
      data-side={side}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* The hover bridge — EXACTLY the gap to the bar, on the bar's side, never over it. */}
      <div className="absolute left-0 right-0 h-2" style={side === 'above' ? { top: '100%' } : { bottom: '100%' }} />
      {children}
    </div>
  ), document.body);
}

/**
 * THE DRAG card. `getAnchor()` returns the dragged bar's ELEMENT (or any object
 * with `getBoundingClientRect` — the crew Gantt passes bar + day strip as one
 * box); `prefer` ('above' | 'below') is `placeGanttTooltip`'s. Its LIVE rect
 * is re-read every animation frame, so the card tracks the bar as it moves or
 * resizes — never the cursor and never the mousedown point. Same placement rule
 * as the hover card. `pointer-events-none`: it can never swallow the drag.
 */
export function GanttDragTooltip({ getAnchor, getMinTop, testId, prefer = 'above', children }) {
  const ref = useRef(null);
  const pointerXRef = useRef(null);
  useLayoutEffect(() => {
    let frame = 0;
    // The drag's own pointer — the card stays within the distance limit of it.
    const onMove = (ev) => { pointerXRef.current = ev.clientX; };
    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('mousemove', onMove, true);
    const place = () => {
      const el = ref.current;
      const bar = getAnchor && getAnchor();
      if (el && bar) {
        const next = placeGanttTooltip(rectOf(bar.getBoundingClientRect()), el.getBoundingClientRect(), {
          minTop: getMinTop ? getMinTop() : 0,
          pointerX: pointerXRef.current,
          prefer,
        });
        el.style.left = `${next.left}px`;
        el.style.top = `${next.top}px`;
        el.style.visibility = 'visible';
        el.dataset.side = next.side;
      }
      if (typeof requestAnimationFrame === 'function') frame = requestAnimationFrame(place);
    };
    place();
    return () => {
      if (frame && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame);
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('mousemove', onMove, true);
    };
  }, [getAnchor, getMinTop, prefer]);
  return createPortal((
    <div
      ref={ref}
      className="fixed pointer-events-none"
      style={{ zIndex: 60, left: 0, top: 0, visibility: 'hidden', width: 'max-content' }}
      data-testid={testId}
    >
      {children}
    </div>
  ), document.body);
}
